/**
 * The only file in `src/` permitted to contain hex literals.
 *
 * Everywhere else, colors come from CSS custom properties. A raw hex in a component will not respond
 * to merchant branding and is therefore wrong for at least one merchant — which is why the rule is
 * enforced rather than encouraged.
 */

export type Scheme = 'light' | 'dark';

export interface Theme {
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

/** Used when branding is absent, a token fails validation, or contrast enforcement needs an anchor. */
export const DEFAULT_THEME: Readonly<Record<Scheme, Readonly<Theme>>> = Object.freeze({
  light: Object.freeze({
    primary: '#1a56db',
    secondary: '#1e429f',
    background: '#ffffff',
    text: '#111827',
  }),
  dark: Object.freeze({
    primary: '#3b82f6',
    secondary: '#60a5fa',
    background: '#121827',
    text: '#f9fafb',
  }),
});

/** Contrast anchors. When a merchant's `text` is unreadable, it is replaced with one of these. */
export const NEAR_BLACK = '#111827';
export const NEAR_WHITE = '#f9fafb';

export const PURE_BLACK = '#000000';
export const PURE_WHITE = '#ffffff';

/**
 * Status colors are **not** merchant-controlled, and this is a deliberate refusal of white-labelling.
 * A merchant whose brand color is red must not get a red success screen: on a payment page, color
 * carries meaning about money, and handing that vocabulary to the merchant would let branding say
 * "paid" when the backend said "rejected".
 *
 * Contrast against the corresponding default background is asserted in `defaults.test.ts`.
 */
export const STATUS_COLORS: Readonly<
  Record<Scheme, Readonly<{ success: string; warning: string; danger: string; info: string }>>
> = Object.freeze({
  light: Object.freeze({
    success: '#047857',
    warning: '#b45309',
    danger: '#b91c1c',
    info: '#1d4ed8',
  }),
  dark: Object.freeze({
    success: '#34d399',
    warning: '#fbbf24',
    danger: '#f87171',
    info: '#93c5fd',
  }),
});

/** A neutral used for the focus ring when the merchant's primary cannot reach 3:1. */
export const FOCUS_RING_FALLBACK: Readonly<Record<Scheme, string>> = Object.freeze({
  light: '#1f2937',
  dark: '#e5e7eb',
});
